import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ActionList, Icon, Popover, Tooltip } from "@shopify/polaris";
import { InfoIcon } from "@shopify/polaris-icons";
import { PiBracketsCurlyBold } from "react-icons/pi";
import { FormTextField } from "../../../components/FormFields";

export interface VariableOption {
    content: string;
    value: string;
}

interface VariableTextFieldProps {
    name: string;
    label: string;
    variables: VariableOption[];
    placeholder?: string;
    maxLength?: number;
    showCharacterCount?: boolean;
    /** Info icon + tooltip shown right beside the label. */
    tooltipContent?: string;
    /** Text shown below the field, same as FormTextField's helpText. */
    helpText?: string;
    disabled?: boolean;
}

// Text field bound to an RHF `name`, with a "{ }" popover (matching VolumeContentForm's
// VariableTextField) that inserts a caller-supplied list of {token} variables at cursor-end.
export const VariableTextField = ({
    name,
    label,
    variables,
    placeholder,
    maxLength,
    showCharacterCount,
    tooltipContent,
    helpText,
    disabled,
}: VariableTextFieldProps) => {
    const { control, setValue } = useFormContext();
    const value = useWatch({ control, name });
    const [popoverActive, setPopoverActive] = useState(false);
    const togglePopoverActive = () => setPopoverActive((active) => !active);

    const handleVariableInsert = (variable: string) => {
        setValue(name, `${value || ""}${variable}`, { shouldDirty: true, shouldValidate: true });
        setPopoverActive(false);
    };

    return (
        <div style={{ width: "100%" }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "4px",
                    marginBottom: "4px",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    {label}
                    {tooltipContent && (
                        <Tooltip content={tooltipContent} width="wide">
                            <Icon source={InfoIcon} tone="subdued" />
                        </Tooltip>
                    )}
                </div>
                <Popover
                    active={popoverActive}
                    activator={
                        <div onClick={togglePopoverActive} style={{ cursor: "pointer", display: "flex" }}>
                            <Tooltip content="Add variable">
                                <PiBracketsCurlyBold />
                            </Tooltip>
                        </div>
                    }
                    onClose={togglePopoverActive}
                >
                    <ActionList
                        items={variables.map((variable) => ({
                            content: variable.content,
                            onAction: () => handleVariableInsert(variable.value),
                        }))}
                    />
                </Popover>
            </div>
            <FormTextField
                name={name}
                label=""
                labelHidden
                placeholder={placeholder}
                maxLength={maxLength}
                showCharacterCount={showCharacterCount}
                helpText={helpText}
                disabled={disabled}
            />
        </div>
    );
};

export default VariableTextField;
